'use client';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
// Phone auth not yet migrated to Better Auth - keeping old import for now
import { useAuthActions } from '@convex-dev/auth/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ChevronDown, Loader2, Phone } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// Country data with flags and codes
const countries = [
  { name: 'United States', code: '+1', flag: '🇺🇸', iso: 'US' },
  { name: 'United Kingdom', code: '+44', flag: '🇬🇧', iso: 'GB' },
  { name: 'Canada', code: '+1', flag: '🇨🇦', iso: 'CA' },
  { name: 'Australia', code: '+61', flag: '🇦🇺', iso: 'AU' },
  { name: 'Germany', code: '+49', flag: '🇩🇪', iso: 'DE' },
  { name: 'France', code: '+33', flag: '🇫🇷', iso: 'FR' },
  { name: 'Italy', code: '+39', flag: '🇮🇹', iso: 'IT' },
  { name: 'Spain', code: '+34', flag: '🇪🇸', iso: 'ES' },
  { name: 'Netherlands', code: '+31', flag: '🇳🇱', iso: 'NL' },
  { name: 'Belgium', code: '+32', flag: '🇧🇪', iso: 'BE' },
  { name: 'Switzerland', code: '+41', flag: '🇨🇭', iso: 'CH' },
  { name: 'Austria', code: '+43', flag: '🇦🇹', iso: 'AT' },
  { name: 'Sweden', code: '+46', flag: '🇸🇪', iso: 'SE' },
  { name: 'Norway', code: '+47', flag: '🇳🇴', iso: 'NO' },
  { name: 'Denmark', code: '+45', flag: '🇩🇰', iso: 'DK' },
  { name: 'Finland', code: '+358', flag: '🇫🇮', iso: 'FI' },
  { name: 'Poland', code: '+48', flag: '🇵🇱', iso: 'PL' },
  { name: 'Czech Republic', code: '+420', flag: '🇨🇿', iso: 'CZ' },
  { name: 'Hungary', code: '+36', flag: '🇭🇺', iso: 'HU' },
  { name: 'Portugal', code: '+351', flag: '🇵🇹', iso: 'PT' },
  { name: 'Greece', code: '+30', flag: '🇬🇷', iso: 'GR' },
  { name: 'Turkey', code: '+90', flag: '🇹🇷', iso: 'TR' },
  { name: 'Russia', code: '+7', flag: '🇷🇺', iso: 'RU' },
  { name: 'China', code: '+86', flag: '🇨🇳', iso: 'CN' },
  { name: 'Japan', code: '+81', flag: '🇯🇵', iso: 'JP' },
  { name: 'South Korea', code: '+82', flag: '🇰🇷', iso: 'KR' },
  { name: 'India', code: '+91', flag: '🇮🇳', iso: 'IN' },
  { name: 'Singapore', code: '+65', flag: '🇸🇬', iso: 'SG' },
  { name: 'Hong Kong', code: '+852', flag: '🇭🇰', iso: 'HK' },
  { name: 'Taiwan', code: '+886', flag: '🇹🇼', iso: 'TW' },
  { name: 'Thailand', code: '+66', flag: '🇹🇭', iso: 'TH' },
  { name: 'Malaysia', code: '+60', flag: '🇲🇾', iso: 'MY' },
  { name: 'Indonesia', code: '+62', flag: '🇮🇩', iso: 'ID' },
  { name: 'Philippines', code: '+63', flag: '🇵🇭', iso: 'PH' },
  { name: 'Vietnam', code: '+84', flag: '🇻🇳', iso: 'VN' },
  { name: 'Brazil', code: '+55', flag: '🇧🇷', iso: 'BR' },
  { name: 'Mexico', code: '+52', flag: '🇲🇽', iso: 'MX' },
  { name: 'Argentina', code: '+54', flag: '🇦🇷', iso: 'AR' },
  { name: 'Chile', code: '+56', flag: '🇨🇱', iso: 'CL' },
  { name: 'Colombia', code: '+57', flag: '🇨🇴', iso: 'CO' },
  { name: 'Peru', code: '+51', flag: '🇵🇪', iso: 'PE' },
  { name: 'South Africa', code: '+27', flag: '🇿🇦', iso: 'ZA' },
  { name: 'Egypt', code: '+20', flag: '🇪🇬', iso: 'EG' },
  { name: 'Nigeria', code: '+234', flag: '🇳🇬', iso: 'NG' },
  { name: 'Kenya', code: '+254', flag: '🇰🇪', iso: 'KE' },
  { name: 'Morocco', code: '+212', flag: '🇲🇦', iso: 'MA' },
  { name: 'Israel', code: '+972', flag: '🇮🇱', iso: 'IL' },
  { name: 'UAE', code: '+971', flag: '🇦🇪', iso: 'AE' },
  { name: 'Saudi Arabia', code: '+966', flag: '🇸🇦', iso: 'SA' },
  { name: 'Qatar', code: '+974', flag: '🇶🇦', iso: 'QA' },
  { name: 'Kuwait', code: '+965', flag: '🇰🇼', iso: 'KW' },
  { name: 'New Zealand', code: '+64', flag: '🇳🇿', iso: 'NZ' },
].sort((a, b) => a.name.localeCompare(b.name));

const phoneAuthSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^\d{7,15}$/, 'Please enter a valid phone number (7-15 digits)'),
  name: z.string().optional(),
});

const verificationSchema = z.object({
  verificationCode: z.string().length(6, 'Verification code must be 6 digits'),
});

type PhoneAuthFormType = z.infer<typeof phoneAuthSchema>;
type VerificationFormType = z.infer<typeof verificationSchema>;

interface PhoneAuthFormProps {
  onBack: () => void;
  onSuccess: () => void;
  isSignUp?: boolean;
}

export function PhoneAuthForm({
  onBack,
  onSuccess,
  isSignUp = false,
}: PhoneAuthFormProps) {
  const [step, setStep] = useState<'phone' | 'verification'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((c) => c.iso === 'US')!
  ); // Default to United States
  const [open, setOpen] = useState(false);

  const { signIn } = useAuthActions();

  const phoneForm = useForm<PhoneAuthFormType>({
    resolver: zodResolver(phoneAuthSchema),
    defaultValues: {
      phoneNumber: '',
      name: '',
    },
  });

  const verificationForm = useForm<VerificationFormType>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      verificationCode: '',
    },
  });

  // Remove phone existence check since Convex Auth handles user creation automatically

  const handlePhoneSubmit = async (values: PhoneAuthFormType) => {
    setIsLoading(true);
    setError(null);

    try {
      // Combine country code with phone number
      const formattedPhone = selectedCountry.code + values.phoneNumber;

      // Send OTP using Convex Auth - first call to signIn sends the code
      await signIn('twilio', {
        phone: formattedPhone,
      });

      setPhoneNumber(formattedPhone);
      setUserName(values.name || '');
      setStep('verification');
    } catch (err) {
      console.error('Failed to send verification code:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (values: VerificationFormType) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use Convex Auth signIn with Twilio provider - second call with code verifies
      await signIn('twilio', {
        phone: phoneNumber,
        code: values.verificationCode,
      });

      // If successful, onSuccess will be called automatically
      onSuccess();
    } catch (err) {
      console.error('Phone auth error:', err);
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setError(null);
    verificationForm.reset();
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Resend code using Convex Auth
      await signIn('twilio', {
        phone: phoneNumber,
      });
    } catch {
      setError('Failed to resend verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'verification') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToPhone}
              className="h-auto p-1"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <CardTitle className="text-2xl">Verify Phone</CardTitle>
              <CardDescription>
                Enter the 6-digit code sent to {phoneNumber}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form
            onSubmit={verificationForm.handleSubmit(handleVerificationSubmit)}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="verificationCode">Verification Code</Label>
              <Input
                id="verificationCode"
                type="text"
                placeholder="123456"
                maxLength={6}
                {...verificationForm.register('verificationCode')}
                className="text-center text-lg tracking-widest"
              />
              {verificationForm.formState.errors.verificationCode && (
                <p className="text-destructive text-sm">
                  {verificationForm.formState.errors.verificationCode.message}
                </p>
              )}
            </div>

            <div className="flex flex-col space-y-2">
              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSignUp ? 'Create Account' : 'Sign In'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleResendCode}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Resend Code
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-auto p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Phone className="h-5 w-5" />
              {isSignUp ? 'Sign Up' : 'Sign In'} with Phone
            </CardTitle>
            <CardDescription>
              {isSignUp
                ? 'Create an account using your phone number'
                : 'Sign in using your phone number'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Phone existence check removed - Convex Auth handles user creation automatically */}

        <form
          onSubmit={phoneForm.handleSubmit(handlePhoneSubmit)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <div className="flex">
              {/* Country Code Selector */}
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-[140px] justify-between rounded-r-none border-r-0"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg">{selectedCountry.flag}</span>
                      <span className="text-sm">{selectedCountry.code}</span>
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <Command>
                    <CommandInput placeholder="Search country..." />
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup className="max-h-[200px] overflow-auto">
                      {countries.map((country) => (
                        <CommandItem
                          key={country.iso}
                          value={`${country.name} ${country.code}`}
                          onSelect={() => {
                            setSelectedCountry(country);
                            setOpen(false);
                          }}
                        >
                          <span className="flex w-full items-center gap-3">
                            <span className="text-lg">{country.flag}</span>
                            <span className="flex-1">{country.name}</span>
                            <span className="text-muted-foreground text-sm">
                              {country.code}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Phone Number Input */}
              <Input
                id="phoneNumber"
                type="tel"
                placeholder="Enter phone number"
                className="rounded-l-none"
                {...phoneForm.register('phoneNumber')}
                autoFocus
              />
            </div>
            {phoneForm.formState.errors.phoneNumber && (
              <p className="text-destructive text-sm">
                {phoneForm.formState.errors.phoneNumber.message}
              </p>
            )}
          </div>

          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name (Optional)</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                {...phoneForm.register('name')}
              />
              {phoneForm.formState.errors.name && (
                <p className="text-destructive text-sm">
                  {phoneForm.formState.errors.name.message}
                </p>
              )}
            </div>
          )}

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Verification Code
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
